import { createApi } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import Cookies from "js-cookie";
import getTenantId from "../../components/Tenent";
import { showAccessDeniedModal } from "../slices/authSlice";
const getStore = () => require("../store").store;

const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_ALGO_API}`,
  withCredentials: true,
  headers: {
    accept: `application/json`,
  },
});

const setUpInterceptors = (getState, dispatch) => {
  axiosInstance.interceptors.request.use(
    (config) => {
      const token = Cookies.get("access_token");
      const refresh_token = Cookies.get("refresh_token");
      const tenantId = getTenantId();

      config.headers["x-tenant-id"] = tenantId;

      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
        config.headers["Refresh"] = `Bearer ${refresh_token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  axiosInstance.interceptors.response.use(
    (res) => res,
    async (err) => {
      const originalConfig = err.config;
      const errorMessage = err.response?.data?.error || "";

      if (
        err.response?.status === 401 &&
        errorMessage.includes("access token has expired") &&
        !originalConfig._retry
      ) {
        originalConfig._retry = true;
        const accessToken = Cookies.get("access_token");
        const refreshToken = Cookies.get("refresh_token");
        if (!refreshToken) {
          Cookies.remove("access_token");
          Cookies.remove("refresh_token");
          return Promise.reject(err);
        }

        try {
          // Determine the correct tenant ID
          const tenantId = getTenantId();

          const refreshHeaders = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${refreshToken}`,
            "x-tenant-id": tenantId,
          };
          // Request new tokens from `/core/refresh`
          const { data } = await axios.post(
            `${process.env.REACT_APP_AUTH_API}/core/refresh`,
            {
              access_token: accessToken,
              refresh_token: refreshToken,
            },
            { headers: refreshHeaders },
          );

          // Save new tokens in Cookies
          Cookies.set("access_token", data?.access_token, {
            secure: true,
          });
          Cookies.set("refresh_token", data?.refresh_token, {
            secure: true,
          });

          // Retry the original request with the new token
          originalConfig.headers["Authorization"] =
            `Bearer ${data?.access_token}`;
          originalConfig.headers["Refresh"] = `Bearer ${data?.refresh_token}`;
          return axiosInstance(originalConfig);
        } catch (_error) {
          // Refresh failed, clear cookies and redirect to login
          Cookies.remove("access_token");
          Cookies.remove("refresh_token");
          return Promise.reject(_error);
        }
      }

      return Promise.reject(err);
    },
  );
};

const axiosBaseQuery =
  () =>
  async (
    { url, method, body, params, ...requestOpts },
    { getState, dispatch },
  ) => {
    setUpInterceptors(getState, dispatch);

    const tenantId = getTenantId();

    const enhancedHeaders = {
      ...requestOpts.headers,
      "X-Tenant-Id": tenantId,
    };

    try {
      const axiosOptions = {
        url: url,
        method,
        data: body,
        params,
        headers: enhancedHeaders,
      };

      const result = await axiosInstance(axiosOptions);
      return { data: result.data };
    } catch (axiosError) {
      let err = axiosError;
      const errorData = axiosError.response?.data;
      const status = axiosError.response?.status;

      if (status === 403 && errorData?.error === "forbidden") {
        getStore().dispatch(showAccessDeniedModal());
      }
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

const shouldInvalidateLifecycle = (result, error) => {
  const response = result || error?.data || {};

  // Do not refetch lifecycle when backend asks for custom conversion
  if (response?.requires_custom_conversion === true) {
    return [];
  }

  // Do not refetch lifecycle for failed API calls
  if (error) {
    return [];
  }

  // Normal successful create/update/delete
  return ["lifecycle"];
};
export const pcfManagementApi = createApi({
  reducerPath: "pcfManagementApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["PcfManagement", "lifecycle"],
  endpoints: (builder) => ({
    productList: builder.query({
      query: ({ last_updated_sort, status, search, page, limit }) => {
        return {
          url: "/pcf/get_products",
          params: {
            ...(last_updated_sort ? { last_updated_sort } : {}),
            ...(status ? { status } : {}),
            ...(search ? { search } : {}),
            ...(page ? { page } : {}),
            ...(limit ? { limit } : {}),
          },
        };
      },
      providesTags: ["list"],
    }),
    pcfSummary: builder.query({
      query: () => {
        return {
          url: "/pcf/products_summary",
        };
      },
      providesTags: ["summary"],
    }),
    defineProduct: builder.mutation({
      query: (body) => {
        return {
          url: "/pcf/create_product",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["define", "list", "summary"],
    }),
    // in your service
    updateDefineProduct: builder.mutation({
      query: ({ id, body }) => ({
        url: `/pcf/product/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["define", "list", "summary"],
    }),
    publishProduct: builder.mutation({
      query: ({ id, body }) => ({
        url: `/pcf/product/publish/${id}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ['list', 'summary']
    }),
    deleteProduct: builder.mutation({
      query: (id) => {
        return {
          url: `/pcf/delete_product/${id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["list", "summary"],
    }),
    createGroup: builder.mutation({
      query: (body) => ({
        url: `/pcf/create_group`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["group"],
    }),

    getGroups: builder.query({
      query: ({ id, stage_id }) => ({
        url: `/pcf/product/${id}/groups`,
        params: stage_id ? { stage_id } : {},
      }),
      providesTags: ["group"],
    }),
    activities: builder.query({
      query: ({
        search_text_factor,
        page,
        limit,
        units_list,
        region_list,
        source_list,
        year_filter,
      } = {}) => ({
        url: `/pcf/unit_activity_explorer_pcf`,
        params: {
          ...(search_text_factor && { search_text_factor }),
          ...(limit && { limit }),
          ...(page && { page }),
          ...(units_list && { units_list }),
          ...(region_list && { region_list }),
          ...(source_list && { source_list }),
          ...(year_filter && { year_filter }),
        },
      }),
    }),
    activityFilters: builder.query({
      query: () => {
        return {
          url: `/pcf/pcf_activity_explorer_filters`,
        };
      },
    }),
    createActivity: builder.mutation({
      query: (body) => {
        return {
          url: `/pcf/create_activity`,
          body,
          method: "POST",
        };
      },
      invalidatesTags: (result, error) =>
        shouldInvalidateLifecycle(result, error),
      // invalidatesTags: ['lifecycle']
    }),
    getStages: builder.query({
      query: () => {
        return {
          url: `/pcf/get_stages`,
        };
      },
    }),
    updateActivity: builder.mutation({
      query: ({ id, body, productId }) => {
        console.log("RTK UPDATE ACTIVITY BODY:", {
          id,
          body,
        });

        return {
          url: `/pcf/update_activity/${id}`,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: (result, error) =>
        shouldInvalidateLifecycle(result, error),
      invalidatesTags: ['lifecycle']
    }),
    deleteActivity: builder.mutation({
      query: ({ id }) => ({
        url: `/pcf/delete_activity/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["lifecycle"],
    }),
    getProductDetails: builder.query({
      query: (id) => {
        return {
          url: `/pcf/get_product/${id}`,
        };
      },
      providesTags: ["define"],
    }),
    getProductLifecycle: builder.query({
      query: (id) => {
        return {
          url: `/pcf/product/${id}/lifecycle`,
        };
      },
      providesTags: ["lifecycle"],
    }),
    getUnits: builder.query({
      query: () => {
        return {
          url: `/pcf/units_dropdown`,
        };
      },
    }),
  }),
});

export const {
  useProductListQuery,
  usePcfSummaryQuery,
  useDefineProductMutation,
  useUpdateDefineProductMutation,
  usePublishProductMutation,
  useDeleteProductMutation,
  useCreateGroupMutation,
  useGetGroupsQuery,
  useActivitiesQuery,
  useLazyActivityFiltersQuery,
  useActivityFiltersQuery,
  useCreateActivityMutation,
  useGetStagesQuery,
  useUpdateActivityMutation,
  useDeleteActivityMutation,
  useGetProductDetailsQuery,
  useGetProductLifecycleQuery,
  useGetUnitsQuery,
  useLazyGetGroupsQuery,
} = pcfManagementApi;
